import { useState, useEffect } from "react";
import {
  useCreateUnit,
  useOneUnit,
  useAllUnits,
  useUpdateUnit,
} from "http-hooks/unit/useUnit";
import Select from "components/ui/Select";
import Input from "components/ui/Input";
import Modal from "components/ui/modals";

const createNestedStructure = (units) => {
  const unitMap = units?.reduce((map, unit) => {
    map[unit.unitId] = { ...unit, children: [] };
    return map;
  }, {});
  const root = [];
  units?.forEach((unit) => {
    if (unit.unitidunder !== 0) {
      unitMap[unit.unitidunder].children.push(unitMap[unit.unitId]);
    } else {
      root.push(unitMap[unit.unitId]);
    }
  });
  return root;
};

const flattenNestedStructure = (nestedStructure) => {
  const result = [];
  const flatten = (node) => {
    result.push({ ...node });
    if (node.children && node.children.length > 0) node.children.forEach(flatten);
  };
  nestedStructure.forEach(flatten);
  return result;
};

export default function SetUnit({ id, onClose, open }) {
  const [unitDetails, setUnitDetails] = useState({
    unitname: "",
    unitidunder: 1,
  });
  const [selectedUnit, setSelectedUnit] = useState(null);

  const { isLoading: isAllUnitsLoading, data: { data: allunits } = [] } = useAllUnits({
    unitname: "",
    systemg: "",
  });

  const unitOptions = flattenNestedStructure(createNestedStructure(allunits)).map((unit) => ({
    value: unit.unitId,
    label: unit.unitname,
    ...unit,
  }));

  const { mutateAsync: mutateCreate, isPending: isUnitCreating } = useCreateUnit();
  const { mutateAsync: mutateUpdate, isPending: isUnitUpdating } = useUpdateUnit();
  const { isLoading, data } = useOneUnit(id);

  const isPending = isUnitCreating || isUnitUpdating;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUnitDetails((prevData) => ({ ...prevData, [name]: value }));
  };

  useEffect(() => {
    if (selectedUnit) {
      setUnitDetails((prevData) => ({ ...prevData, unitidunder: selectedUnit.value }));
    }
  }, [selectedUnit]);

  useEffect(() => {
    if (data && allunits) {
      const unitUnder = allunits?.find((unit) => unit.unitId == data.unitidunder);
      if (unitUnder) {
        setSelectedUnit({ value: unitUnder.unitId, label: unitUnder.unitname, ...unitUnder });
      }
      setUnitDetails((prev) => {
        const updatedDetails = {};
        Object.keys(prev).forEach((key) => {
          updatedDetails[key] = data[key] ?? prev[key];
        });
        return updatedDetails;
      });
    }
  }, [data, allunits]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (id) {
        await mutateUpdate({ id, data: unitDetails });
      } else {
        await mutateCreate(unitDetails);
      }
      onClose();
    } catch (error) {
      console.error("Operation failed:", error);
    }
  };

  return (
    <Modal
      open={open}
      setOpen={(val) => !val && onClose()}
      header={
        <h3 className="text-base font-semibold leading-6 text-gray-900">
          {id ? "Update" : "Add"} Unit
        </h3>
      }
      footer={
        <div className="flex justify-end gap-x-3">
          <button
            type="button"
            className="text-sm font-semibold rounded-md px-3 py-2 hover:bg-gray-100 text-gray-900"
            onClick={onClose}
            disabled={isPending}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="set-unit-form"
            disabled={isPending}
            className="rounded-md bg-gray-700 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isPending ? "Saving..." : "Save"}
          </button>
        </div>
      }
    >
      {id && isLoading ? (
        <div className="animate-pulse space-y-6">
          <div className="h-9 bg-gray-200 rounded w-full" />
          <div className="h-9 bg-gray-200 rounded w-full" />
        </div>
      ) : (
        <form id="set-unit-form" onSubmit={handleSubmit} className="space-y-6">
          <Input
            type="text"
            name="unitname"
            id="unitname"
            label="Unit Name"
            maxLength="10"
            value={unitDetails.unitname}
            onChange={handleInputChange}
          />
          <Select
            options={unitOptions}
            value={selectedUnit}
            onChange={setSelectedUnit}
            placeholder="Select Unit Under"
            label="Unit Under"
            isLoading={isAllUnitsLoading}
          />
        </form>
      )}
    </Modal>
  );
}
